/**
 * Product Notify Me Integration for Shopify
 * Complete mobile-optimized version with all modal handling
 */

class ProductNotifyMe {
  constructor() {
    this.n8nWebhookUrl = 'https://primary-production-2641.up.railway.app/webhook/notify-me';
    this.debugMode = false; // Set to false in production
    this.retryCount = 0;
    this.maxRetries = 2;
    this.init();
  }

  init() {
    this.log('Initializing ProductNotifyMe...');
    
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    this.log('Setting up event listeners');
    
    // Handle notify me button clicks with event delegation
    document.addEventListener('click', (e) => {
      // Handle notify button
      const notifyButton = e.target.closest('.js-notify-me-button');
      if (notifyButton) {
        e.preventDefault();
        e.stopPropagation();
        this.log('Notify button clicked');
        this.handleNotifyMeClick(notifyButton);
        return;
      }

      // Handle modal toggle
      const toggleBtn = e.target.closest('[data-js-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        const targetId = toggleBtn.getAttribute('data-js-toggle');
        const modal = document.getElementById(targetId);
        if (modal) {
          this.openModal(modal);
        }
        return;
      }

      // Handle modal close
      const closeBtn = e.target.closest('[data-js-close]');
      if (closeBtn) {
        e.preventDefault();
        const targetId = closeBtn.getAttribute('data-js-close');
        const modal = document.getElementById(targetId);
        if (modal) {
          this.closeModal(modal);
        }
        return;
      }
    }, true); // Use capture phase

    // Handle form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('js-notify-me-form')) {
        e.preventDefault();
        e.stopPropagation();
        this.log('Form submit intercepted');
        this.handleFormSubmit(e.target);
      }
    }, true); // Use capture phase

    // Close modal on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const activeModal = document.querySelector('.product-notify-me-modal.is-active');
        if (activeModal) {
          this.closeModal(activeModal);
        }
      }
    });

    this.log('Event listeners setup complete');
  }

  log(message, data = null) {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    
    console.log(logMessage, data || '');
    
    if (this.debugMode) {
      // Create debug panel if it doesn't exist
      let debugEl = document.getElementById('notify-me-debug');
      if (!debugEl) {
        debugEl = document.createElement('div');
        debugEl.id = 'notify-me-debug';
        debugEl.style.cssText = 'position: fixed; bottom: 0; left: 0; right: 0; max-height: 300px; overflow-y: auto; background: #000; color: #0f0; font-size: 11px; padding: 10px; z-index: 99999; font-family: monospace; line-height: 1.4;';
        debugEl.innerHTML = '<button onclick="this.parentElement.remove()" style="float: right; background: red; color: white; border: none; padding: 2px 8px; cursor: pointer;">✕</button><div style="font-weight: bold; margin-bottom: 5px;">DEBUG LOG:</div>';
        document.body.appendChild(debugEl);
      }
      
      const entry = document.createElement('div');
      entry.textContent = `${logMessage} ${data ? JSON.stringify(data, null, 2) : ''}`;
      debugEl.appendChild(entry);
      debugEl.scrollTop = debugEl.scrollHeight;
    }
  }

  handleNotifyMeClick(button) {
    this.log('handleNotifyMeClick called');
    
    const targetId = button.getAttribute('data-target') || button.getAttribute('data-js-toggle');
    this.log('Target modal ID:', targetId);
    
    const modal = document.getElementById(targetId);
    
    if (!modal) {
      this.log('ERROR: Modal not found!', targetId);
      alert('Error: Modal not found. Please refresh the page.');
      return;
    }

    // Get product data from button or page
    const productData = {
      productId: button.getAttribute('data-product-id') || this.getProductData().productId,
      variantId: button.getAttribute('data-variant-id') || this.getProductData().variantId,
      productTitle: button.getAttribute('data-product-title') || this.getProductData().productTitle,
      variantTitle: button.getAttribute('data-variant-title') || this.getProductData().variantTitle
    };
    
    this.log('Product data:', productData);
    
    // Store data in modal
    modal.setAttribute('data-product-id', productData.productId);
    modal.setAttribute('data-variant-id', productData.variantId);
    modal.setAttribute('data-product-title', productData.productTitle);
    modal.setAttribute('data-variant-title', productData.variantTitle);
    
    // Open modal
    this.openModal(modal);
  }

  getProductData() {
    // Try multiple methods to get product data
    const metaElement = document.querySelector('[data-product-json]');
    if (metaElement) {
      try {
        const productJson = JSON.parse(metaElement.textContent);
        const selectedVariant = this.getSelectedVariant(productJson);
        
        return {
          productId: productJson.id.toString(),
          variantId: selectedVariant.id.toString(),
          productTitle: productJson.title,
          variantTitle: selectedVariant.title !== 'Default Title' ? selectedVariant.title : ''
        };
      } catch (e) {
        this.log('Error parsing product JSON:', e.message);
      }
    }

    // Fallback methods
    const variantInput = document.querySelector('select[name="id"], input[name="id"]');
    const productIdMeta = document.querySelector('meta[property="og:product:id"]');
    
    return {
      productId: productIdMeta ? productIdMeta.content : '',
      variantId: variantInput ? variantInput.value : '',
      productTitle: document.querySelector('.product__title, h1')?.textContent?.trim() || '',
      variantTitle: ''
    };
  }

  getSelectedVariant(productJson) {
    const variantInput = document.querySelector('select[name="id"], input[name="id"]');
    const selectedVariantId = variantInput ? parseInt(variantInput.value) : null;
    
    if (selectedVariantId) {
      const variant = productJson.variants.find(v => v.id === selectedVariantId);
      if (variant) return variant;
    }
    
    return productJson.variants[0];
  }

  async handleFormSubmit(form) {
    this.log('=== FORM SUBMIT STARTED ===');
    
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const modal = form.closest('[data-product-id]');
    
    this.log('Email input value:', emailInput?.value);
    this.log('Modal found:', !!modal);
    
    if (!emailInput || !emailInput.value) {
      this.log('ERROR: Email validation failed');
      this.showError(form, 'Please enter a valid email address');
      return;
    }

    if (!modal) {
      this.log('ERROR: Modal not found');
      this.showError(form, 'Error: Unable to find product information');
      return;
    }

    const email = emailInput.value.trim();
    const productId = modal.getAttribute('data-product-id');
    const variantId = modal.getAttribute('data-variant-id');
    const productTitle = modal.getAttribute('data-product-title');
    const variantTitle = modal.getAttribute('data-variant-title');

    const payload = {
      email: email,
      product_id: productId,
      variant_id: variantId,
      product_title: productTitle,
      variant_title: variantTitle
    };

    this.log('Request payload:', payload);

    // Disable button
    const originalButtonText = submitButton?.textContent || 'Notify Me';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }

    try {
      this.log('Attempting to send request...');
      const response = await this.sendRequest(payload);
      
      this.log('Response received:', response);

      const result = Array.isArray(response) ? response[0] : response;
      if (result && result.success) {
        this.log('✓ SUCCESS!');
        this.showSuccess(form);
        
        setTimeout(() => {
          this.closeModal(modal);
          form.reset();
        }, 2000);
      } else {
        this.log('✗ Response indicated failure');
        this.showError(form, 'Something went wrong. Please try again.');
      }
    } catch (error) {
      this.log('✗ CRITICAL ERROR:', error.message);
      this.log('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      this.showError(form, this.debugMode 
        ? `Error: ${error.message}` 
        : 'Unable to process request. Please try again.');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
      this.log('=== FORM SUBMIT COMPLETED ===');
    }
  }

  async sendRequest(payload) {
    this.log(`Sending to: ${this.n8nWebhookUrl}`);
    
    // Try XMLHttpRequest first (better iOS compatibility)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', this.n8nWebhookUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.timeout = 15000; // 15 second timeout
      
      xhr.onload = () => {
        this.log('XHR onload - Status:', xhr.status);
        this.log('XHR response text:', xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            this.log('JSON parse error:', e.message);
            reject(new Error('Invalid response format'));
          }
        } else {
          reject(new Error(`Server returned ${xhr.status}: ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = () => {
        this.log('XHR onerror triggered');
        reject(new Error('Network error - unable to reach server'));
      };
      
      xhr.ontimeout = () => {
        this.log('XHR timeout');
        reject(new Error('Request timed out'));
      };
      
      xhr.onabort = () => {
        this.log('XHR aborted');
        reject(new Error('Request was aborted'));
      };
      
      this.log('Sending XHR request...');
      xhr.send(JSON.stringify(payload));
    });
  }

  showSuccess(form) {
  this.log('Showing success message');
  const successMessage = form.querySelector('.js-notify-me-success');
  const formFields = form.querySelector('.js-notify-me-fields');
  
  if (formFields) formFields.style.display = 'none';
  if (successMessage) successMessage.style.display = 'block';
}

  showError(form, message) {
    this.log('Showing error:', message);
    const errorElement = form.querySelector('.js-notify-me-error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
      
      setTimeout(() => {
        errorElement.style.display = 'none';
      }, 5000);
    } else {
      alert(message);
    }
  }

  openModal(modal) {
    this.log('Opening modal:', modal.id);
    modal.style.display = 'flex';
    // Force reflow
    modal.offsetHeight;
    modal.classList.add('is-active');
    document.body.classList.add('modal-open');
    
    // Focus first input
    setTimeout(() => {
      const firstInput = modal.querySelector('input[type="email"]');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }

  closeModal(modal) {
    this.log('Closing modal:', modal.id);
    modal.classList.remove('is-active');
    document.body.classList.remove('modal-open');
    
    // Reset form after animation
    setTimeout(() => {
      const form = modal.querySelector('.js-notify-me-form');
      if (form) {
        form.reset();
        const successMessage = form.querySelector('.js-notify-me-success');
        const formFields = form.querySelector('.js-notify-me-fields');
        const errorElement = form.querySelector('.js-notify-me-error');
        
        if (successMessage) successMessage.style.display = 'none';
        if (formFields) formFields.style.display = 'block';
        if (errorElement) errorElement.style.display = 'none';
      }
      modal.style.display = 'none';
    }, 300);
  }
}

// Initialize immediately
(function() {
  console.log('ProductNotifyMe script loaded');
  window.productNotifyMeInstance = new ProductNotifyMe();
})();